import React, { useState, useEffect } from 'react';
import { X, Star, Trophy, ArrowRight } from 'lucide-react';
import { Database } from '../lib/supabase';
import Logo from './Logo';

type Activity = Database['public']['Tables']['activities']['Row'];

interface ActivitySimulatorProps {
  activity: Activity;
  onComplete: (score: number, timeSpent: number) => void;
  onClose: () => void;
}

// Simple math problems for demonstration
const mathProblems = [
  { question: "5 + 3 = ?", options: ["6", "8", "7", "9"], correct: 1 },
  { question: "12 - 4 = ?", options: ["8", "7", "9", "6"], correct: 0 },
  { question: "3 × 4 = ?", options: ["12", "15", "10", "14"], correct: 0 },
  { question: "16 ÷ 4 = ?", options: ["3", "4", "5", "6"], correct: 1 },
];

const readingProblems = [
  { question: "Which word rhymes with 'cat'?", options: ["dog", "hat", "fish", "bird"], correct: 1 },
  { question: "What is the first letter of 'apple'?", options: ["A", "B", "C", "D"], correct: 0 },
  { question: "Which word means the opposite of 'big'?", options: ["large", "huge", "small", "tall"], correct: 2 },
  { question: "How many syllables are in 'butterfly'?", options: ["2", "3", "4", "5"], correct: 1 },
];

const scienceProblems = [
  { question: "What do plants need to grow?", options: ["Only water", "Sunlight and water", "Only soil", "Only air"], correct: 1 },
  { question: "Which animal is a mammal?", options: ["Fish", "Bird", "Dog", "Snake"], correct: 2 },
  { question: "What causes rain?", options: ["Wind", "Clouds", "Sun", "Moon"], correct: 1 },
  { question: "Which planet is closest to the sun?", options: ["Earth", "Mars", "Mercury", "Venus"], correct: 2 },
];

export function ActivitySimulator({ activity, onComplete, onClose }: ActivitySimulatorProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [startTime] = useState(Date.now());
  const [problems, setProblems] = useState<typeof mathProblems>([]);
  const [showFeedback, setShowFeedback] = useState<'correct' | 'incorrect' | null>(null);

  useEffect(() => {
    // Select problems based on activity subject
    const selectedProblems = (() => {
      switch (activity.subject) {
        case 'math':
          return mathProblems;
        case 'reading':
          return readingProblems;
        case 'science':
          return scienceProblems;
        default:
          return mathProblems;
      }
    })();
    
    // Shuffle and take first 4 problems
    const shuffled = [...selectedProblems].sort(() => 0.5 - Math.random()).slice(0, 4);
    setProblems(shuffled);
  }, [activity.subject]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNextQuestion = () => {
    const isCorrect = selectedAnswer === problems[currentQuestion]?.correct;
    
    // Show feedback
    setShowFeedback(isCorrect ? 'correct' : 'incorrect');
    
    // Update score
    if (isCorrect) {
      setScore(prev => prev + 25);
    }

    // Wait for feedback animation
    setTimeout(() => {
      setShowFeedback(null);
      
      if (currentQuestion < problems.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
      } else {
        // Activity completed
        const timeSpent = Math.floor((Date.now() - startTime) / 1000);
        const finalScore = selectedAnswer === problems[currentQuestion]?.correct ? score + 25 : score;
        setShowResult(true);
        
        // Call onComplete after a short delay to show the result
        setTimeout(() => {
          onComplete(finalScore, timeSpent);
        }, 2000);
      }
    }, 1500);
  };

  if (problems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (showResult) {
    const finalScore = selectedAnswer === problems[currentQuestion]?.correct ? score + 25 : score;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <div className="mx-auto mb-4">
              <Logo size="xl" variant="icon-only" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {finalScore >= 75 ? 'Excellent!' : finalScore >= 50 ? 'Good Job!' : 'Keep Practicing!'}
            </h2>
            <p className="text-gray-600">You scored {finalScore} out of 100 points!</p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Activity Complete!</h3>
            <div className="flex justify-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-500" />
                <span>{activity.points_reward} points earned</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentProblem = problems[currentQuestion];
  if (!currentProblem) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo size="lg" variant="icon-only" />
              <div>
                <h2 className="text-xl font-bold">{activity.title}</h2>
                <p className="text-blue-100">Question {currentQuestion + 1} of {problems.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / problems.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {currentProblem.question}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {currentProblem.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                className={`p-4 rounded-lg border-2 text-left font-medium transition-all duration-200 ${
                  selectedAnswer === index
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                    selectedAnswer === index
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-gray-300 text-gray-500'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Score: {score} / 100
            </div>
            
            <button
              onClick={handleNextQuestion}
              disabled={selectedAnswer === null}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              <span>{currentQuestion < problems.length - 1 ? 'Next' : 'Finish'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Overlay */}
      {showFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-4">
              <Logo size="xl" variant="icon-only" />
            </div>
            {showFeedback === 'correct' ? (
              <>
                <h3 className="text-2xl font-bold text-green-600 mb-2">That's right!</h3>
                <p className="text-gray-700">Great job! You got it correct!</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-orange-600 mb-2">Not quite right</h3>
                <p className="text-gray-700">That's okay! Let's try the next one!</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}